import { inject, Injectable, signal, WritableSignal } from '@angular/core';
import { TranslocoService } from '@jsverse/transloco';

import { Layer, LayerOptions, Project } from '@connect-services/src/sdk/api';
import WfsExtensionEnum = LayerOptions.WfsExtensionEnum;

import ProjectionEnum = Project.ProjectionEnum;

type WmsVersion = '1.0.0' | '1.1.0' | '1.1.1' | '1.3.0';
type WfsVersion = '1.0.0' | '1.1.0' | '2.0.0';

export enum UrlSourceEnum {
    WFS = 'wfs',
    WMS = 'wms',
    WMTS = 'wmts',
}

export enum WmsStyleEnum {
    DEFAULT = '',
    ESTOMPAGE_GRAYSCALE = 'estompage_grayscale',
    HYPSO = 'hypso',
    HYPSO_COLOR = 'hypso_color',
    HYPSO_GRAYSCALE = 'hypso_grayscale',
    HYPSO_NOALPHA = 'hypso_noalpha',
    HYPSO_NOALPHA_COLOR = 'hypso_noalpha_color',
    HYPSO_NOALPHA_GRAYSCALE = 'hypso_noalpha_grayscale',
    NORMAL = 'normal',
}

export class UrlSourceLayer {
    public constructor(
        public name: string,
        public title: string,
        public availableProjections?: Set<string>,
    ) {}
}

@Injectable({
    providedIn: 'root',
})
export class UrlSourceService {
    private static readonly mainElementTagName: Record<WmsVersion, string> = {
        '1.0.0': 'WMT_MS_Capabilities',
        '1.1.0': 'WMT_MS_Capabilities',
        '1.1.1': 'WMT_MS_Capabilities',
        '1.3.0': 'WMS_Capabilities',
    };
    private static readonly projectionsElementTagName: Record<WmsVersion, string> = {
        '1.0.0': 'SRS',
        '1.1.0': 'SRS',
        '1.1.1': 'SRS',
        '1.3.0': 'CRS',
    };

    public readonly detectedVersion: WritableSignal<WfsVersion | null> = signal(null);
    public readonly urlSourceType: WritableSignal<UrlSourceEnum | null> = signal(null);
    public readonly wfsExtension: WritableSignal<WfsExtensionEnum | null> = signal(null);

    private readonly completeParamsList: string[] = [
        'service',
        'request',
        'version',
        'layers',
        'crs',
        'srs',
        'bbox',
        'width',
        'height',
        'format',
        'transparent',
        'styles',
    ];
    private readonly flowMapping: Record<'wms' | 'wfs', { service: string; version: WmsVersion | WfsVersion }> = {
        wfs: { service: 'WFS', version: '2.0.0' },
        wms: { service: 'WMS', version: '1.3.0' },
    };
    private readonly suffixToRemove = '/annexes/ressources';
    private readonly translocoService = inject(TranslocoService);
    private version!: WmsVersion | WfsVersion;
    private xml?: Document;
    private XMLparser = new DOMParser();

    public generateGetCapabilitiesUrl(rawUrl: string): string {
        const url = this.normalizeUrl(rawUrl);

        return `${this.getInitialUrl(url)}?${this.getVersionUrl(url)}&SERVICE=WMS&REQUEST=GetCapabilities`;
    }

    public generateGetFeatureWFSUrl(rawUrl: string, layerName: string): string {
        const url = this.normalizeUrl(rawUrl);

        let originUrl: string = this.getInitialUrl(url);

        const userKey: string | null = this.getKey(url);

        const version: WfsVersion = this.detectedVersion() ?? '2.0.0';

        if (originUrl.includes(this.suffixToRemove)) {
            originUrl = originUrl.replace(this.suffixToRemove, '');
        }

        if (this.wfsExtension() === WfsExtensionEnum.GEOJSON) {
            return `${originUrl}?SERVICE=WFS&VERSION=${version}&REQUEST=GetFeature&typeName=${layerName}&outputFormat=json`;
        }

        if (userKey) {
            return `${originUrl}?key=${userKey}&SERVICE=WFS&VERSION=${version}&REQUEST=GetFeature&typeName=${layerName}&srsName=EPSG:4326`;
        }

        return `${originUrl}?SERVICE=WFS&VERSION=1.1.0&REQUEST=GetFeature&typeName=${layerName}`;
    }

    public generateWMSUrlWithReplacementToken(
        rawUrl: string,
        layerName: string,
        projection: string,
        dimensions: number,
        format: string = 'image/png',
    ): string {
        const url = this.normalizeUrl(rawUrl);

        const version: WmsVersion = (this.version as WmsVersion) || (this.getVersionUrl(url) as WmsVersion) || '1.3.0';
        const crsKey = version === '1.3.0' ? 'CRS' : 'SRS';

        const input = new URL(url);
        const baseUrl: string = this.formatWMSUrl(url, input.origin);

        const preservedParams: string[] = this.getPreservedParams(input);

        const style: WmsStyleEnum = this.getLayerStyle(layerName);

        return `${baseUrl}?${[
            ...preservedParams,
            'SERVICE=WMS',
            `VERSION=${version}`,
            'REQUEST=GetMap',
            `LAYERS=${layerName}`,
            `${crsKey}=${projection}`,
            `BBOX=${this.projectionToReplacementToken(projection)}`,
            `WIDTH=${dimensions}`,
            `HEIGHT=${dimensions}`,
            `FORMAT=${format}`,
            'TRANSPARENT=TRUE',
            `STYLES=${style}`,
        ].join('&')}`;
    }

    public getLayerName(url: string): string {
        const urlToUppercase: string = url.toUpperCase();

        const params: URLSearchParams = new URL(urlToUppercase).searchParams;

        const layerName: string | null = params.get('LAYERS') ?? params.get('LAYER');

        if (!layerName) {
            throw new Error(this.translocoService.translate('noWmsLayerAvailable'));
        }

        return layerName;
    }

    public async getLegendUrlFromXml(url: string, layerName: string): Promise<string | null> {
        const result: string = await this.getXmlContent(url);

        const parser = new DOMParser();
        const srcDom: Document = parser.parseFromString(result, 'text/xml');

        srcDom.querySelector('parsererror');

        const layerElement: Element | undefined = Array.from(srcDom.getElementsByTagName('Layer')).find(
            (element: Element) => {
                return element.querySelector(':scope > Name')!.innerHTML === layerName;
            },
        );

        if (!layerElement) {
            return null;
        }

        const urlElement: Element | null = layerElement.querySelector(':scope > Style > LegendURL > OnlineResource');

        if (!urlElement) {
            return null;
        }

        const legendUrl: Attr | null = urlElement.attributes.getNamedItem('xlink:href');

        if (!legendUrl) {
            return null;
        }

        return legendUrl.value;
    }

    public async load(url: string, selectedFlow: Layer.TypeEnum): Promise<void> {
        const formatedUrl: string = this.formatBaseUrl(url, selectedFlow);
        const body: string = await this.getXmlContent(formatedUrl);

        const xml: Document = this.XMLparser.parseFromString(body, 'text/xml');

        this.version = this.getVersionUrl(formatedUrl);

        const isWfsVersion = ['1.0.0', '1.1.0', '2.0.0'].includes(this.version);
        if (isWfsVersion) {
            this.detectedVersion.set(this.version as WfsVersion);
        }

        this.wfsExtension.set(this.extractFormatFromXml(xml));

        this.xml = xml;
    }

    public parse(): UrlSourceLayer[] {
        if (!this.xml) {
            return [];
        }

        if (this.urlSourceType() === UrlSourceEnum.WMS) {
            const mainContainer: Element | null = this.xml.querySelector(
                `${UrlSourceService.mainElementTagName[this.version as WmsVersion]} > Capability > Layer`,
            );

            return mainContainer ? this.getWMSLayers(mainContainer, new Set<string>()) : [];
        }

        this.checkOutputFormat();

        const mainContainer: Element | null = this.xml.querySelector('FeatureTypeList');

        return mainContainer ? this.getWFSLayers(mainContainer) : [];
    }

    private buildCurrentWmsLayer(layerEl: Element): { name: string; title: string } {
        const name: string = (layerEl.querySelector(':scope > Name')?.textContent ?? '').trim();
        const title: string = (layerEl.querySelector(':scope > Title')?.textContent ?? '').trim() || name;

        return { name, title };
    }

    private checkOutputFormat(): void {
        if (!this.wfsExtension()) {
            throw new Error('formatNotSupported');
        }
    }

    private collectBoundingBoxCrs(element: Element): Set<string> {
        const bboxCodes = new Set<string>();
        const bboxElems: NodeListOf<Element> = element.querySelectorAll(':scope > BoundingBox');

        bboxElems.forEach((bbox: Element): void => {
            const bboxAttribute: string = (bbox.getAttribute('CRS') || bbox.getAttribute('SRS') || '').trim();
            if (!bboxAttribute) {
                return;
            }

            bboxCodes.add(bboxAttribute.toUpperCase());
        });

        return bboxCodes;
    }

    private collectChildLayers(parentElement: Element, effectiveCrs: Set<string>): UrlSourceLayer[] {
        const childElements: NodeListOf<Element> = parentElement.querySelectorAll(':scope > Layer');

        return Array.from(childElements).flatMap((child: Element): UrlSourceLayer[] =>
            this.getWMSLayers(child, effectiveCrs),
        );
    }

    private collectDeclaredCrs(element: Element, crsTag: string): Set<string> {
        const codes = new Set<string>();
        const nodes: NodeListOf<Element> = element.querySelectorAll(`:scope > ${crsTag}`);

        nodes.forEach((node: Element): void => {
            const projection: string = (node.textContent ?? '').trim();

            if (!projection) {
                return;
            }

            projection
                .split(/\s+/)
                .map((code: string): string => code.trim().toUpperCase())
                .filter(Boolean)
                .forEach((code: string): Set<string> => codes.add(code));
        });

        return codes;
    }

    private extractFormatFromXml(xml: Document): WfsExtensionEnum | null {
        const operationFeature: Element | undefined = Array.from(xml.getElementsByTagNameNS('*', 'Operation')).find(
            (operation: Element) => operation.getAttribute('name') === 'GetFeature',
        );

        if (!operationFeature) {
            return null;
        }

        const outputFormatParam: Element | undefined = Array.from(
            operationFeature.getElementsByTagNameNS('*', 'Parameter'),
        ).find((param: Element) => param.getAttribute('name') === 'outputFormat');

        if (!outputFormatParam) {
            return null;
        }

        const formats: string[] = Array.from(outputFormatParam.getElementsByTagNameNS('*', 'Value')).map(
            (element: Element): string => element.textContent?.toLowerCase() ?? '',
        );

        if (formats.some((format: string) => format.includes('json'))) {
            return WfsExtensionEnum.GEOJSON;
        }

        if (formats.some((format: string) => format.includes('gml'))) {
            return WfsExtensionEnum.GML;
        }

        return null;
    }

    private formatBaseUrl(rawUrl: string, selectedFlow: Layer.TypeEnum): string {
        let urlObj: URL;
        let url;

        try {
            url = this.normalizeUrl(rawUrl);

            urlObj = new URL(url);
        } catch {
            throw new Error(this.translocoService.translate('invalidFlow'));
        }

        if (url.toLowerCase().endsWith('.xml')) {
            return url;
        }

        const requestParam = urlObj.searchParams.get('REQUEST') ?? urlObj.searchParams.get('request');

        if (requestParam?.toUpperCase() === 'GETCAPABILITIES') {
            this.version = this.getVersionUrl(url);

            return url;
        }

        if (!this.isFluxLayerType(selectedFlow)) {
            throw new Error(this.translocoService.translate('invalidLayerType'));
        }

        const { service, version } = this.flowMapping[selectedFlow];

        urlObj.searchParams.set('SERVICE', service);
        urlObj.searchParams.set('VERSION', version);
        urlObj.searchParams.set('REQUEST', 'GetCapabilities');

        return urlObj.toString();
    }

    private formatWMSUrl(baseUrl: string, fallbackOrigin: string): string {
        const url = new URL(baseUrl, fallbackOrigin);

        const pathnameLower: string = url.pathname.toLowerCase();
        const isXml: boolean = pathnameLower.endsWith('.xml');
        const hasWmsR: boolean = pathnameLower.includes('/wms-r/');

        if (isXml && hasWmsR) {
            url.pathname = '/wms-r/wms';
            url.search = '';
        }

        this.stripReservedParams(url);

        url.search = '';

        return `${url.origin}${url.pathname}`;
    }

    private getInitialUrl(url: string): string {
        const urlInstance = new URL(url);

        return `${urlInstance.origin}${urlInstance.pathname}`;
    }

    private getKey(url: string): string | null {
        try {
            const params: URLSearchParams = new URL(url).searchParams;

            return params.get('key');
        } catch {
            return null;
        }
    }

    private getLayerElementByName(name: string): Element | null {
        const layers: Element[] = Array.from(this.xml!.getElementsByTagName('Layer'));

        return layers.find((layer: Element) => layer.querySelector(':scope > Name')?.textContent === name) ?? null;
    }

    private getLayerStyle(layerName: string): WmsStyleEnum {
        const styles: string[] = this.getLayerStyles(layerName).map((style: string) => style.toLowerCase());

        switch (true) {
            case styles.includes(WmsStyleEnum.ESTOMPAGE_GRAYSCALE):
                return WmsStyleEnum.ESTOMPAGE_GRAYSCALE;
            case styles.includes(WmsStyleEnum.HYPSO):
                return WmsStyleEnum.HYPSO;
            case styles.includes(WmsStyleEnum.HYPSO_COLOR):
                return WmsStyleEnum.HYPSO_COLOR;
            case styles.includes(WmsStyleEnum.HYPSO_GRAYSCALE):
                return WmsStyleEnum.HYPSO_GRAYSCALE;
            case styles.includes(WmsStyleEnum.HYPSO_NOALPHA):
                return WmsStyleEnum.HYPSO_NOALPHA;
            case styles.includes(WmsStyleEnum.HYPSO_NOALPHA_COLOR):
                return WmsStyleEnum.HYPSO_NOALPHA_COLOR;
            case styles.includes(WmsStyleEnum.HYPSO_NOALPHA_GRAYSCALE):
                return WmsStyleEnum.HYPSO_NOALPHA_GRAYSCALE;
            case styles.includes(WmsStyleEnum.NORMAL):
                return WmsStyleEnum.NORMAL;
            default:
                return WmsStyleEnum.DEFAULT;
        }
    }

    private getLayerStyles(layerName: string): string[] {
        const layer: Element | null = this.getLayerElementByName(layerName);

        if (!layer) {
            return [];
        }

        const layerNames: Element[] = Array.from(layer.querySelectorAll(':scope > Style > Name'));

        return layerNames.map((layerName: Element): string => layerName.textContent?.trim() || '').filter(Boolean);
    }

    private getPreservedParams(input: URL): string[] {
        const reservedParams = new Set(this.completeParamsList.map((param: string) => param.toLowerCase()));

        return Array.from(input.searchParams.entries())
            .filter(([key]) => !reservedParams.has(key.toLowerCase()))
            .map(([key, value]) => `${key}=${value}`);
    }

    private getSourceInfo(element: Element): { name: string; title: string } | undefined {
        const nameElement = element.querySelector(':scope > Name');
        const titleElement = element.querySelector(':scope > Title');

        const name = nameElement?.innerHTML || titleElement?.innerHTML;
        if (!name) {
            return;
        }

        return { name, title: titleElement?.innerHTML || name };
    }

    private getVersionUrl(url: string): WmsVersion | WfsVersion {
        const params: URLSearchParams = new URL(url).searchParams;
        const explicitVersion: string | null = params.get('VERSION') ?? params.get('version');

        if (explicitVersion) {
            return explicitVersion as WmsVersion | WfsVersion;
        }

        return (this.urlSourceType() === UrlSourceEnum.WMS ? '1.3.0' : '2.0.0') as WmsVersion | WfsVersion;
    }

    private getWFSLayers(mainContainer: Element): UrlSourceLayer[] {
        const nestedElements: NodeListOf<Element> = mainContainer.querySelectorAll('FeatureType');

        return Array.from(nestedElements).flatMap((feature: Element): UrlSourceLayer | [] => {
            const projection: Element | null = feature.querySelector('DefaultCRS') ?? feature.querySelector('OtherSRS');

            if (!projection) {
                return [];
            }

            const projectionValue = projection.textContent ?? '';
            if (
                !projectionValue.includes(ProjectionEnum._4326) &&
                !projectionValue.includes(ProjectionEnum._2154) &&
                !projectionValue.includes(ProjectionEnum._3945)
            ) {
                return [];
            }

            const sourceInfo: { name: string; title: string } | undefined = this.getSourceInfo(feature);
            if (!sourceInfo) {
                return [];
            }

            return new UrlSourceLayer(sourceInfo.name, sourceInfo.title);
        });
    }

    private getWMSLayers(layerEl: Element, incrementedCrs: Set<string>): UrlSourceLayer[] {
        const crsTag: string = UrlSourceService.projectionsElementTagName[this.version as WmsVersion];

        const localCrs: Set<string> = this.readLocalCrs(layerEl, crsTag);

        const effectiveCrs: Set<string> = localCrs.size ? localCrs : incrementedCrs;

        const children: UrlSourceLayer[] = this.collectChildLayers(layerEl, effectiveCrs);

        if (!this.isQueryableLayer(layerEl)) {
            return children;
        }

        const { name, title } = this.buildCurrentWmsLayer(layerEl);

        const projectionsForOutput: Set<string> | undefined = effectiveCrs.size ? new Set(effectiveCrs) : undefined;

        return [new UrlSourceLayer(name, title, projectionsForOutput), ...children];
    }

    private async getXmlContent(url: string): Promise<string> {
        try {
            const response: Response = await fetch(url);

            return response.text();
        } catch {
            throw new Error(
                this.translocoService.translate(
                    this.urlSourceType() === UrlSourceEnum.WFS ? 'noWfsUrlAvailable' : 'noWmsUrlAvailable',
                ),
            );
        }
    }

    private isFluxLayerType(value: Layer.TypeEnum): value is 'wms' | 'wfs' {
        return value === Layer.TypeEnum.WMS || value === Layer.TypeEnum.WFS;
    }

    private isQueryableLayer(el: Element): boolean {
        const name: string = (el.querySelector(':scope > Name')?.textContent ?? '').trim();

        return !!name.length;
    }

    private normalizeUrl(input: string, defaultProtocol: 'https:' | 'http:' = 'https:'): string {
        if (/^https?:\/\//i.test(input)) {
            return input;
        }
        if (input.startsWith('//')) {
            return `${defaultProtocol}${input}`;
        }

        return `${defaultProtocol}//${input}`;
    }

    private projectionToReplacementToken(projection: string): string {
        const [namespace, identifier] = projection.split(':');

        return `{bbox-${namespace.toLowerCase()}-${identifier}}`;
    }

    private readLocalCrs(element: Element, crsTag: string): Set<string> {
        const crsCodes = new Set<string>();

        this.collectDeclaredCrs(element, crsTag).forEach((code: string): Set<string> => crsCodes.add(code));

        this.collectBoundingBoxCrs(element).forEach((code: string): Set<string> => crsCodes.add(code));

        return crsCodes;
    }

    private stripReservedParams(url: URL): void {
        const paramsToRemove = new Set(['service', 'request', 'version']);

        for (const key of Array.from(url.searchParams.keys())) {
            if (paramsToRemove.has(key.toLowerCase())) {
                url.searchParams.delete(key);
            }
        }
    }
}
